import JSZip from "jszip";
import { convertObjectIntoJsonFile, parseSvgFile } from ".";
import {
  IsFileTooBig,
  manageFileType,
  massageFileName,
} from "../../components/AssetManager/AssetManagerUtils/UploaderFunctions";
import { APPLICATION_JSON } from "../../components/constants/mimeTypes";
import { ASSET_TYPE_ID } from "../../const";
import IAsset from "../../models/IAsset";

const smartObjectJsonFilename = "smartObject.json";
const settingsFilename = "settings.json";

/**
 * Prepares the smart object for upload.
 * The backend expects a .zip with the following files:
 * - request.json
 * - file.svg
 * - smartObject.json
 * - settings.json
 * @param svgFile - the svg file for upload
 * @param requestFile - Request file using when uploading a smart object for the first time
 */
export const prepareSmartObjectForUpload = async (svgFile: File, requestFile?: File) => {
  const smartObjectFile = await parseSvgFile(svgFile);
  const mutableFile = new File([svgFile], massageFileName(svgFile));
  const mutableSmartObject = new File([smartObjectFile], massageFileName({ name: smartObjectJsonFilename }), {
    type: APPLICATION_JSON,
  });

  const [filename] = svgFile.name.split(".");

  const settings = {
    type: "svg",
    files: {
      svg: svgFile.name,
      json: smartObjectJsonFilename,
    },
  };

  const json = convertObjectIntoJsonFile(settings, "settings");
  const mutableSettingsJson = new File([json], massageFileName({ name: settingsFilename }), { type: APPLICATION_JSON });

  // check if valid extension
  const validFileType = manageFileType(mutableFile, ASSET_TYPE_ID.SMART_OBJECT);

  if (!validFileType) {
    throw new Error("Invalid file type");
  }

  if (IsFileTooBig(svgFile)) {
    throw new Error("File is too big");
  }

  const data = new FormData();
  data.append("file", mutableFile);
  data.append("smartObject", mutableSmartObject);
  const settingsFile = new File([mutableSettingsJson], "settings.json", { type: "application/json" });
  data.append("settings", settingsFile);
  if (requestFile) {
    data.append("request", requestFile);
  }

  const zip = new JSZip();

  for (const [key, value] of data.entries()) {
    if (value instanceof Blob) {
      // for blob and file objects
      zip.file(value.name, value);
    } else {
      // for json objects
      zip.file(key, new Blob([JSON.stringify(value)], { type: "application/json" }));
    }
  }

  // generate the zip file
  const zipFile = await zip.generateAsync({ type: "blob" });

  const adjustedFileName = filename.replace(/\s+/g, "_");

  return {
    zipFile,
    zipFileName: adjustedFileName + "_data.zip",
  };
};

export const fetchSmartObjectJson = async (asset: IAsset) => {
  const response = await fetch(`/api/Asset/${asset.blobPath}/smartobject.json`);
  return response.json();
};
